<template>
  <div class="topic-preview">
    <!-- Empty State -->
    <div v-if="!topic" class="no-selection has-text-centered p-6">
      <span class="icon is-large has-text-grey-light">
        <i class="fas fa-book fa-3x"></i>
      </span>
      <p class="mt-4 has-text-grey">Select a topic to preview</p>
    </div>

    <!-- Topic Content -->
    <template v-else>
      <!-- Header -->
      <div class="panel-heading">
        <div class="is-flex is-align-items-center">
          <span class="title is-5 mb-0">{{ topic.title }} - (overview)</span>
          <b-tag
            v-if="topic.isPublic === false"
            type="is-warning is-light"
            class="ml-2"
          >
            <b-icon icon="lock" size="is-small" class="mr-1" />
            Private
          </b-tag>
        </div>
        <div class="is-flex is-align-items-center">
          <b-button
            v-if="canEdit"
            label="Edit"
            type="is-warning"
            icon-left="edit"
            size="is-small"
            class="mr-2"
            @click="$emit('edit', topic)"
          />

          <b-button
            v-if="canEdit"
            label="Manage"
            type="is-info"
            icon-left="cog"
            size="is-small"
            class="mr-2"
            @click="goToTopicPage"
          />

          <button
            v-if="$listeners.close"
            type="button"
            class="delete"
            @click="$emit('close')"
          />
        </div>
      </div>

      <!-- Topic Metadata -->
      <TopicMetadata :topic="topic" :scenario-count="scenarios.length" />

      <!-- Scenarios Preview -->
      <div class="scenarios-preview p-3 scrollable-section">
        <div
          v-if="!scenarios || scenarios.length === 0"
          class="has-text-centered p-4"
        >
          <p class="has-text-grey">No examples available for this topic</p>
        </div>

        <div v-else class="board-previews">
          <BoardPreview
            v-for="(scenario, index) in scenarios"
            :key="scenario.id || index"
            :scenario="scenario"
            :index="index"
            :is-selected="selectedScenarioId === scenario.id"
            :board-size="152"
            @click="handleScenarioClick"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BoardPreview from "@/components/training/explorer/molecules/BoardPreview.vue";
import TopicMetadata from "@/components/training/explorer/molecules/TopicMetadata.vue";

export default {
  name: "TopicPreview",

  components: {
    BoardPreview,
    TopicMetadata,
  },

  props: {
    topic: {
      type: Object,
      default: null,
    },
    scenarios: {
      type: Array,
      default: () => [],
    },
    selectedScenarioId: {
      type: String,
      default: null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    goToTopicPage() {
      if (this.topic && this.topic.id) {
        // Check if we're in teacher context
        const isTeacherContext = this.$route.meta?.teacher ?? false;
        const topicPath = isTeacherContext
          ? `/teacher/topics/${this.topic.id}`
          : `/topics/${this.topic.id}`;
        this.$router.push(topicPath);
      }
    },

    handleScenarioClick(scenarioId) {
      this.$emit("select-scenario", scenarioId);
    },
  },
};
</script>

<style scoped>
.topic-preview {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  overflow: hidden; /* Ensures content doesn't overflow */
}

.panel-heading {
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.scrollable-section {
  flex: 1; /* Takes up remaining space */
  overflow-y: auto;
  min-height: 200px; /* Ensures a minimum height for the scenarios section */
}

.board-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}
</style>
